import { Typography } from '@mui/material'
import React, { memo, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getDriver } from 'src/selectors/driver'
import { getIsPartnerAccessRestricted, getIsPartnerFetchPending } from 'src/selectors/partner'
import tw from 'twin.macro'
import {
  ContractorPortalFetchDriver,
  ContractorPortalFetchPartners,
} from 'src/constants/actionTypes'
import { showToast } from 'src/utils/toast'
import { InfoContainer } from 'src/components/shared/InfoContainer'
import { ChevronLeftMinor, EmailMajor, PhoneMajor } from '@shopify/polaris-icons'
import { Button } from 'src/components/shared/Button'
import { ContractorPortalSecureWrapper } from 'src/components/layout/contractorPortal/ContractorPortalSecureWrapper'
import { Navigate, useNavigate } from 'react-router-dom'
import { CONTRACTOR_PORTAL_ROUTES } from 'src/constants/routes'
import { Footer } from 'src/components/shared/Footer'
import { ContractorPortalVerifyPhoneModal } from 'src/components/contractor-portal/contractor-portal-verify-phone-modal/ContractorPortalVerifyPhoneModal'
import { ContractorPortalUpdatePhoneModal } from 'src/components/contractor-portal/contractor-portal-update-phone-modal/ContractorPortalUpdatePhoneModal'
import { useModalOpen } from 'src/hooks/useModalOpen'
import { ContractorPortalUpdateEmailModal } from 'src/components/contractor-portal/contractor-portal-update-email-modal/ContractorPortalUpdateEmailModal'
import { ContractorPortalUpdatePhoneEnterCodeModal } from 'src/components/contractor-portal/contractor-portal-update-phone-enter-code-modal/ContractorPortalUpdatePhoneEnterCodeModal'
import { ContractorPortalUpdateEmailEnterCodeModal } from 'src/components/contractor-portal/contractor-portal-update-email-enter-code-modal/ContractorPortalUpdateEmailEnterCodeModal'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { Driver } from 'src/interfaces/driver'
import { handleApiError } from 'src/utils/errorHandlers'

const ContractorPortalContactDetailsPageComponent = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const driver = useSelector(getDriver)
  const isPartnerAccessRestricted = useSelector(getIsPartnerAccessRestricted)
  const isPartnerFetchPending = useSelector(getIsPartnerFetchPending)

  const [verificationPhoneToken, setVerificationPhoneToken] = useState<string | null>(null)
  const [verificationEmailToken, setVerificationEmailToken] = useState<string | null>(null)
  const [newPhone, setNewPhone] = useState<string | null>(null)
  const [newEmail, setNewEmail] = useState<string | null>(null)

  const {
    isOpen: isVerifyPhonePhoneModalOpen,
    open: openVerifyPhonePhoneModal,
    close: closeVerifyPhonePhoneModal,
  } = useModalOpen()
  const {
    isOpen: isVerifyPhoneEmailModalOpen,
    open: openVerifyPhoneEmailModal,
    close: closeVerifyPhoneEmailModal,
  } = useModalOpen()
  const {
    isOpen: isUpdatePhoneModalOpen,
    open: openUpdatePhoneModal,
    close: closeUpdatePhoneModal,
  } = useModalOpen()
  const {
    isOpen: isUpdateEmailModalOpen,
    open: openUpdateEmailModal,
    close: closeUpdateEmailModal,
  } = useModalOpen()
  const {
    isOpen: isUpdateEnterCodePhoneModalOpen,
    open: openUpdateEnterCodePhoneModal,
    close: closeUpdateEnterCodePhoneModal,
  } = useModalOpen()
  const {
    isOpen: isUpdateEnterCodeEmailModalOpen,
    open: openUpdateEnterCodeEmailModal,
    close: closeUpdateEnterCodeEmailModal,
  } = useModalOpen()

  const handleBack = useCallback(() => {
    navigate(CONTRACTOR_PORTAL_ROUTES.HOME_URL)
  }, [])

  const handleSuccessVerifyPhonePhone = useCallback((token: string) => {
    setVerificationPhoneToken(token)
    closeVerifyPhonePhoneModal()
    openUpdatePhoneModal()
  }, [])

  const handleSuccessVerifyPhoneEmail = useCallback((token: string) => {
    setVerificationEmailToken(token)
    closeVerifyPhoneEmailModal()
    openUpdateEmailModal()
  }, [])

  const handleSuccessUpdatePhone = useCallback((phone: string) => {
    setNewPhone(phone)
    openUpdateEnterCodePhoneModal()
    closeUpdatePhoneModal()
  }, [])

  const handleSuccessUpdateEmail = useCallback((email: string) => {
    setNewEmail(email)
    openUpdateEnterCodeEmailModal()
    closeUpdateEmailModal()
  }, [])

  const handleSuccessUpdateEnterCodePhone = useCallback(async () => {
    closeUpdateEnterCodePhoneModal()

    try {
      const driver: Driver = await createAsyncAction(
        dispatch,
        ContractorPortalFetchDriver.request(),
      )

      dispatch(ContractorPortalFetchPartners.request({ phone: driver.phone }))

      showToast('Phone number updated', {
        variant: 'success',
      })
    } catch (error) {
      handleApiError(error)
    }
  }, [])

  const handleSuccessUpdateEnterCodeEmail = useCallback(() => {
    closeUpdateEnterCodeEmailModal()

    dispatch(ContractorPortalFetchDriver.request())

    showToast('Email address updated', {
      variant: 'success',
    })
  }, [])

  if (!driver || isPartnerFetchPending) {
    return <div />
  }

  if (isPartnerAccessRestricted) {
    return <Navigate to={CONTRACTOR_PORTAL_ROUTES.HOME_URL} replace />
  }

  return (
    <ContractorPortalSecureWrapper>
      <div css={tw`h-full flex w-full flex-col px-4 pt-20 sm:pt-6 pb-12`}>
        <div>
          <Button
            css={tw`bg-primary-100 h-[42px] w-[82px] hover:bg-primary-200`}
            color="light"
            size="medium"
            leftIcon={<ChevronLeftMinor width={19} css={tw`fill-primary-500`} />}
            onClick={handleBack}
            data-testid="header-nav-back-btn"
          >
            Back
          </Button>
          <Typography css={tw`mb-2 mt-4`} variant="h2">
            Contact details
          </Typography>
          <Typography css={tw`mb-4 text-shades-400`}>
            Keep your contact details up to date. Use the options below to update your phone number
            or email address.
          </Typography>
          <InfoContainer>
            Note: Your updated contact details will also apply to all connected partners (if any).
          </InfoContainer>
          <div
            css={tw`rounded-2xl overflow-hidden bg-shades-0 mt-4`}
            data-testid="contractors-list-contact-table"
          >
            <div
              css={tw`p-4 flex cursor-pointer items-center bg-shades-0 justify-between hover:bg-shades-100 active:bg-shades-200`}
              onClick={openVerifyPhonePhoneModal}
              data-testid="contractors-list-contact-phone"
            >
              <div css={tw`flex items-center`}>
                <div
                  css={tw`h-11 w-11 rounded-[22px] items-center justify-center flex bg-shades-50 mr-4`}
                >
                  <PhoneMajor width={20} height={20} />
                </div>
                <div>
                  <Typography variant="h4" css={tw`mb-0.5`}>
                    Phone Number
                  </Typography>
                  <Typography css={tw`mb-0.5 text-shades-400`}>{driver.phone}</Typography>
                </div>
              </div>
              <Typography css={tw`text-primary-500`} fontWeight={600}>
                Update
              </Typography>
            </div>
            <div css={tw`h-[1px] bg-shades-100 mx-4`} />
            <div
              css={tw`p-4 flex cursor-pointer items-center bg-shades-0 justify-between hover:bg-shades-100 active:bg-shades-200`}
              onClick={openVerifyPhoneEmailModal}
              data-testid="contractors-list-contact-email"
            >
              <div css={tw`flex items-center w-3/4`}>
                <div
                  css={tw`h-11 w-11 rounded-[22px] items-center justify-center flex bg-shades-50 mr-4`}
                >
                  <EmailMajor width={20} height={20} />
                </div>
                <div css={tw`w-2/3`}>
                  <Typography variant="h4" css={tw`mb-0.5`}>
                    Email
                  </Typography>
                  <Typography css={tw`mb-0.5 text-shades-400 break-words text-ellipsis`}>
                    {driver.email}
                  </Typography>
                </div>
              </div>
              <Typography css={tw`text-primary-500`} fontWeight={600}>
                Update
              </Typography>
            </div>
          </div>
        </div>
      </div>
      {isVerifyPhonePhoneModalOpen && (
        <ContractorPortalVerifyPhoneModal
          onClose={closeVerifyPhonePhoneModal}
          onSuccess={handleSuccessVerifyPhonePhone}
        />
      )}
      {isUpdatePhoneModalOpen && verificationPhoneToken && (
        <ContractorPortalUpdatePhoneModal
          token={verificationPhoneToken}
          onClose={closeUpdatePhoneModal}
          onSuccess={handleSuccessUpdatePhone}
        />
      )}
      {isUpdateEnterCodePhoneModalOpen && verificationPhoneToken && newPhone && (
        <ContractorPortalUpdatePhoneEnterCodeModal
          token={verificationPhoneToken}
          phone={newPhone}
          onClose={closeUpdateEnterCodePhoneModal}
          onSuccess={handleSuccessUpdateEnterCodePhone}
        />
      )}
      {isVerifyPhoneEmailModalOpen && (
        <ContractorPortalVerifyPhoneModal
          onClose={closeVerifyPhoneEmailModal}
          onSuccess={handleSuccessVerifyPhoneEmail}
        />
      )}
      {isUpdateEmailModalOpen && verificationEmailToken && (
        <ContractorPortalUpdateEmailModal
          token={verificationEmailToken}
          onClose={closeUpdateEmailModal}
          onSuccess={handleSuccessUpdateEmail}
        />
      )}
      {isUpdateEnterCodeEmailModalOpen && verificationEmailToken && newEmail && (
        <ContractorPortalUpdateEmailEnterCodeModal
          token={verificationEmailToken}
          email={newEmail}
          onClose={closeUpdateEnterCodeEmailModal}
          onSuccess={handleSuccessUpdateEnterCodeEmail}
        />
      )}
      <Footer
        className={tw`flex mt-auto items-center px-4 pt-2 pb-8 bg-shades-50 justify-center w-full`}
      />
    </ContractorPortalSecureWrapper>
  )
}

export const ContractorPortalContactDetailsPage = memo(ContractorPortalContactDetailsPageComponent)
